import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getPhaseData } from '../../data/programData';
import { getSupersets, getWarmupData, getEmbedUrl } from '../../data/trainingSessionHelpers';
import {
  SESSION_STORAGE_KEY,
  incrementSessionCompletion,
  clearStoredSession,
  buildManualCompletionPayload,
} from '../../utils/sessionCompletion';
import { 
  Dumbbell, 
  Play, 
  ChevronDown, 
  ChevronUp,
  X,
  Flame,
  CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui';

export function SessionOverviewPage() {
  const { phaseId, sessionNum } = useParams();
  const navigate = useNavigate();
  const { aclCare, updateAclCare, user } = useAuth();
  
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showWarmupIntro, setShowWarmupIntro] = useState(false);
  const [showMarkCompleteConfirm, setShowMarkCompleteConfirm] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [markCompleteError, setMarkCompleteError] = useState('');

  const phaseNum = parseInt(phaseId);
  const workoutNum = parseInt(sessionNum);
  const isOptional = !sessionNum;
  
  const phaseData = getPhaseData(phaseNum);
  const supersets = getSupersets(phaseNum, workoutNum);
  const warmupData = getWarmupData(phaseNum);

  const handleStartSession = () => {
    // Initialize a fresh session
    const newSession = {
      date: new Date().toDateString(),
      phaseNum,
      workoutNum,
      phase: 'warmup',
      warmupCompleted: false,
      currentWarmupCategoryIndex: 0,
      currentWarmupExerciseIndex: 0,
      currentSupersetIndex: 0,
      completedSupersets: [],
      setData: {},
      completed: false,
      startedAt: new Date().toISOString(),
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
    
    navigate(`/program/phase/${phaseId}/training/session/${sessionNum}/play`);
  };

  // Mark the workout complete without stepping through every exercise.
  // Records a training session and bumps the completion count exactly like
  // finishing the full player would.
  const handleMarkComplete = async () => {
    setMarkingComplete(true);
    setMarkCompleteError('');

    try {
      await api.createTrainingSession(
        buildManualCompletionPayload({
          userId: user.id,
          phaseNum,
          workoutNum,
          supersets,
        })
      );

      await updateAclCare(incrementSessionCompletion(aclCare, phaseNum, workoutNum));

      // Drop any partially completed run of this same workout
      clearStoredSession(phaseNum, workoutNum);

      navigate(`/program/phase/${phaseId}/training`, { replace: true });
    } catch (err) {
      console.error('[SessionOverviewPage] Error marking session complete:', err);
      setMarkCompleteError('Failed to save completion. Please try again.');
      setMarkingComplete(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Warmup intro video URL - update with actual
  const warmupIntroUrl = phaseData?.warmupIntroUrl || 'https://youtu.be/ZGao2VvEBOs';

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-2">
        <Dumbbell className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">
          {phaseData?.title} - {isOptional ? 'Optional' : `Session ${sessionNum}`}
        </h1>
      </div>
      
      <p className="text-gray-500 mb-6">
        {supersets.length} supersets • ~45-60 min
      </p>

      {/* Warmup Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                <Flame className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Warmup</h2>
                <p className="text-sm text-gray-500">
                  {warmupData?.categories.reduce((sum, cat) => sum + cat.exercises.length, 0) || 0} exercises
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWarmupIntro(true)}
            >
              <Play className="h-4 w-4 mr-1" />
              Intro
            </Button>
          </div>
        </div>

        {/* Warmup Categories */}
        {warmupData?.categories.map((category) => (
          <div key={category.id} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <span className="text-gray-700 font-medium">{category.name}</span>
                <span className="ml-2 text-sm text-gray-400">
                  ({category.exercises.length})
                </span>
              </div>
              {expandedCategory === category.id ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            
            {expandedCategory === category.id && (
              <div className="px-4 pb-4">
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {category.exercises.map((exercise) => (
                    <div 
                      key={exercise.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{exercise.name}</span>
                      <span className="text-gray-400">{exercise.documentation}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Supersets Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <Dumbbell className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Strength Supersets</h2>
              <p className="text-sm text-gray-500">{supersets.length} supersets • 3 sets each</p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {supersets.map((superset, idx) => (
            <div key={superset.id} className="p-4">
              <div className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <span className="text-xs font-medium text-gray-600">{idx + 1}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{superset.description}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {superset.exercise1.name} + {superset.exercise2.name}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {supersets.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            No supersets data available yet.
          </div>
        )}
      </div>

      {/* Start Button */}
      <Button
        onClick={handleStartSession}
        fullWidth
        size="lg"
      >
        <Play className="h-5 w-5 mr-2" />
        Start Workout
      </Button>

      {/* Mark Complete (shortcut for workouts done off-app) */}
      <Button
        variant="outline"
        onClick={() => {
          setMarkCompleteError('');
          setShowMarkCompleteConfirm(true);
        }}
        fullWidth
        className="mt-3"
      >
        <CheckCircle className="h-5 w-5 mr-2" />
        Mark Workout Complete
      </Button>
      <p className="mt-2 text-center text-xs text-gray-500">
        Already did this workout? Log it without going through each exercise.
      </p>

      {/* Mark Complete Confirmation Modal */}
      {showMarkCompleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => !markingComplete && setShowMarkCompleteConfirm(false)}
          />

          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Mark Workout Complete?
              </h2>
              <p className="text-gray-600 mb-6">
                This will count as one completion of {phaseData?.title} - Session {sessionNum} without
                recording individual sets or reps.
              </p>

              {markCompleteError && (
                <p className="text-sm text-red-600 mb-4">{markCompleteError}</p>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowMarkCompleteConfirm(false)}
                  fullWidth
                  disabled={markingComplete}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMarkComplete}
                  fullWidth
                  loading={markingComplete}
                  disabled={markingComplete}
                >
                  Mark Complete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warmup Intro Modal */}
      {showWarmupIntro && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/70"
            onClick={() => setShowWarmupIntro(false)}
          />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Warmup Introduction</h2>
                <button
                  onClick={() => setShowWarmupIntro(false)}
                  className="p-2 -mr-2 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <div className="aspect-video bg-gray-900">
                <iframe
                  src={getEmbedUrl(warmupIntroUrl)}
                  title="Warmup Introduction"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}